import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded'
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined'
import DashboardRounded from '@mui/icons-material/DashboardRounded'
import DashboardOutlined from '@mui/icons-material/DashboardOutlined'
import Dashboard from '@mui/icons-material/Dashboard'
import HubOutlined from '@mui/icons-material/HubOutlined'
import HubRounded from '@mui/icons-material/HubRounded'
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined'
import TaskAlt from '@mui/icons-material/TaskAlt'
import TuneRounded from '@mui/icons-material/TuneRounded'
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined'
import Description from '@mui/icons-material/Description'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import SyncRoundedIcon from '@mui/icons-material/SyncRounded'
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined'
import SmartToy from '@mui/icons-material/SmartToy'
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined'
import GroupAdd from '@mui/icons-material/GroupAdd'
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined'
import Inventory2 from '@mui/icons-material/Inventory2'
import SchoolOutlined from '@mui/icons-material/SchoolOutlined'
import School from '@mui/icons-material/School'
import BookOnlineOutlined from '@mui/icons-material/BookOnlineOutlined'
import BookOnline from '@mui/icons-material/BookOnline'
import WorkOutline from '@mui/icons-material/WorkOutlineRounded'
import WorkRounded from '@mui/icons-material/WorkRounded'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useNotifications } from '../../context/useNotifications'
import { useFocusMode } from '../../context/useFocusMode'
import { useWorkspace } from '../../context/useWorkspace'
import { useIsMemberMobile } from '../../member/useIsMemberMobile'
import { MobileTabBar, MobileTabRow, type MobileTabItem } from '../Common/MobileTabs'
import { MoreTabsSheet } from '../Common/MoreTabsSheet'
import { TabBarEditor } from '../Common/TabBarEditor'
import { useMemberTabBar } from '../../services/navigation/useMemberTabBar'
import { MobileSyncStatusPill } from '../MobileSync/MobileSyncCenter'
import { OverviewPhonePage } from '../Overview/OverviewPhonePage'
import { IncomingInvitesPage } from '../Invites/IncomingInvitesPage'
import { ConfirmActionModal } from '../Common/ConfirmActionModal'
import { ToastContext } from '../../context/toastContextCore'
import { switchToWorkspaceForTarget, requestWorkspaceSwitch } from '../../services/workspaceSwitchService'
import { openWorkspaceTarget } from '../../services/workspaceSwitch/openWorkspaceTarget'
import { setMemberOutboxNotifier } from '../../services/memberOutboxService'
import { SideNavPageShell } from './sidenav/SideNavPageShell'
import { MobileSettingsCommandCenter } from '../MobileSettings/MobileSettingsCommandCenter'
import { ScreenGuideHost } from '../ScreenGuide'
import { TourGuideContext } from '../tourguide/tourGuideContextCore'
import { memberScreenGuideId } from './memberScreenGuides'
import { MemberMenuDrawer } from './MemberMenuDrawer'
import { FocusModeOverlay } from '../Common/FocusMode/FocusModeOverlay'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { emitSensoryEvent } from '../../services/aliveness/SensoryEventBus'
import { MemberNotificationDrawer } from './MemberNotificationDrawer'
import { MemberWorkspaceSwitcher } from './MemberWorkspaceSwitcher'
import { buildMemberPath } from '../../member/memberAppRouting'
import { safeMemberLanding } from '../../services/workspaceSwitch/switchLanding'
import { subscribeToMyOpsPhoneItems } from '../../services/opsPhoneAccessService'
import {
  getMemberOpsPhoneItems,
  setMemberOpsPhoneItemsForWorkspace,
  subscribeMemberOpsStore,
} from '../../services/member/memberOpsStore'
import type { OpsPhoneItemKey } from '../../../functions-common/src/ops/phoneItems'
import type { TabBarPolicy } from '../../services/navigation/tabBarEngine'
import type { Workspace } from '../../types/workspace'
import type { WorkspaceRole } from '../../context/WorkspaceContext'
import type { MemberTabKey } from '../../member/AppMember'
import type { MemberTabKey as CatalogMemberTabKey } from '../../services/navigation/tabCatalog'
import { resolveProfileImageUrl } from '../../utils/profileImage'
import { isPersonalWorkspace, isTeamWorkspace } from '../../utils/workspaceKind'
import { featureFlag } from '../../utils/featureFlags'
import { useNls } from '../../nls/nlsProvider'
import { roleLabelKey } from '../../utils/roleLabels'
import { getMemberTabLabel } from '../Mobile/MobileNls'
import { isSectorBookingsEnabled } from '../../utils/workspaceModules'
import { resolveDigestItem, resolveNotification, type NotificationRouterCtx, type NotificationRouterHandle } from '../../services/notificationRouter'

import {
  MemberLayoutContext,
  type MemberLayoutContextValue,
} from './MemberLayoutContext'

type NavItem = {
  key: MemberTabKey
  label: string
  path: string
  icon: ReactNode
}

type MemberNotificationNavigators = Omit<NotificationRouterCtx, 'workspaceSlug' | 'openIncomingInvites' | 'openMemberSettings' | 'openProfile' | 'openOverview'> & {
  missingNavigators?: ReadonlyArray<'openCreditsLedger' | 'openReport'>
}

const iconStyle = { fontSize: 22 }

// Desktop rail keeps its original shape (Calendar stays a top-level page and
// AI Magic stays inside Controls); only the label changed with the rename.
const makeDesktopNavItems = (workspace: Workspace, t: ReturnType<typeof useNls>['t']): NavItem[] => {
  const items: NavItem[] = [
    { key: 'for-you', label: getMemberTabLabel('for-you', t), path: buildMemberPath({ workspace: workspace.slug, tab: 'for-you' }), icon: <TaskAltOutlined style={iconStyle} /> },
    { key: 'board', label: getMemberTabLabel('board', t), path: buildMemberPath({ workspace: workspace.slug, tab: 'board' }), icon: <DashboardRounded style={iconStyle} /> },
    { key: 'nexus', label: getMemberTabLabel('nexus', t), path: buildMemberPath({ workspace: workspace.slug, tab: 'nexus' }), icon: <HubRounded style={iconStyle} /> },
    { key: 'controls', label: getMemberTabLabel('controls', t), path: buildMemberPath({ workspace: workspace.slug, tab: 'automations' }), icon: <TuneRounded style={iconStyle} /> },
  ]
  if (isSectorBookingsEnabled(workspace)) {
    items.push({
      key: 'bookings',
      label: getMemberTabLabel('bookings', t),
      path: buildMemberPath({ workspace: workspace.slug, tab: 'bookings' }),
      icon: <BookOnlineOutlined style={iconStyle} />,
    })
  }
  return items
}

const getMemberTabIcons = (key: MemberTabKey | CatalogMemberTabKey) => {
  switch (key) {
    case 'for-you':
      return { icon: <TaskAltOutlined style={iconStyle} />, activeIcon: <TaskAlt style={iconStyle} /> }
    case 'board':
      return { icon: <DashboardOutlined style={iconStyle} />, activeIcon: <Dashboard style={iconStyle} /> }
    case 'nexus':
    case 'calendar':
    case 'chat':
      return { icon: <HubOutlined style={iconStyle} />, activeIcon: <HubRounded style={iconStyle} /> }
    case 'ai':
      return { icon: <AutoAwesomeOutlined style={iconStyle} />, activeIcon: <AutoAwesomeRounded style={iconStyle} /> }
    case 'forms':
      return { icon: <DescriptionOutlined style={iconStyle} />, activeIcon: <Description style={iconStyle} /> }
    case 'sync':
      return { icon: <SyncOutlinedIcon style={iconStyle} />, activeIcon: <SyncRoundedIcon style={iconStyle} /> }
    case 'automations':
      return { icon: <SmartToyOutlined style={iconStyle} />, activeIcon: <SmartToy style={iconStyle} /> }
    case 'team':
      return { icon: <GroupAddOutlined style={iconStyle} />, activeIcon: <GroupAdd style={iconStyle} /> }
    case 'stock':
      return { icon: <Inventory2Outlined style={iconStyle} />, activeIcon: <Inventory2 style={iconStyle} /> }
    case 'class':
      return { icon: <SchoolOutlined style={iconStyle} />, activeIcon: <School style={iconStyle} /> }
    case 'bookings':
      return { icon: <BookOnlineOutlined style={iconStyle} />, activeIcon: <BookOnline style={iconStyle} /> }
    case 'ops':
      return { icon: <WorkOutline style={iconStyle} />, activeIcon: <WorkRounded style={iconStyle} /> }
    case 'controls':
    default:
      return { icon: <TuneRounded style={iconStyle} />, activeIcon: <TuneRounded style={iconStyle} /> }
  }
}

const HeaderIconButton = ({
  onClick,
  label,
  showDot,
  children,
  testId,
  unreadLabel,
}: {
  onClick: () => void
  label: string
  showDot?: boolean
  children: ReactNode
  testId?: string
  unreadLabel?: string
}) => (
  <IconButton
    variant="tertiary"
    size="md"
    className="relative"
    label={label}
    onClick={onClick}
    data-testid={testId}
    icon={<>
      {children}
      {showDot ? (
        <span
          className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[var(--danger)]"
          aria-label={unreadLabel ?? 'unread'}
        />
      ) : null}
    </>}
  />
)

export const MemberDashboardLayout = ({
  workspace,
  role,
  activeTab,
  aiMode,
  nexusPanel,
  isProfileOpen,
  children,
  sidecar,
  secondaryTabs,
  onNavigate,
  onSelectWorkspace,
  onOpenProfile,
  notificationNavigators,
  onOpenSyncCenter,
}: {
  workspace: Workspace
  role: WorkspaceRole
  activeTab: MemberTabKey
  aiMode?: string | null
  nexusPanel?: string | null
  isProfileOpen?: boolean
  children: ReactNode
  sidecar: ReactNode
  /** Mobile only: a secondary tab row stacked directly above the primary tabs. */
  secondaryTabs?: ReactNode
  onNavigate: (tab: MemberTabKey, path: string) => void
  onSelectWorkspace?: (workspace: Workspace) => void
  onOpenProfile?: () => void
  notificationNavigators: MemberNotificationNavigators
  onOpenSyncCenter?: () => void
}) => {
  const { t } = useNls()
  const isMobile = useIsMemberMobile()
  const { currentUser, userProfile, logout } = useAuth()
  const displayName = useDisplayName(userProfile ?? currentUser)
  const profileImageUrl = resolveProfileImageUrl(userProfile, currentUser)
  const { workspaces, canPerform, setAppMode } = useWorkspace()
  const { unreadCount } = useNotifications()
  const toastCtx = useContext(ToastContext)
  const showToast = toastCtx?.showToast
  const focusMode = useFocusMode()
  const stageRef = useRef<HTMLElement | null>(null)
  const shellRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLElement>(null)
  const [measuredBottomChromeHeight, setMeasuredBottomChromeHeight] = useState<number | null>(null)
  const [keyboardOpen, setKeyboardOpen] = useState(false)
  const [bottomAddonTarget, setBottomAddonTarget] = useState<HTMLElement | null>(null)

  const scrollStageToTop = useCallback(() => {
    const stage = stageRef.current
    if (!stage) return
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    stage.scrollTo({
      top: 0,
      behavior: prefersReduced ? 'auto' : 'smooth',
    })
  }, [])

  const layoutContextValue = useMemo<MemberLayoutContextValue>(() => ({
    scrollStageToTop,
    bottomAddonTarget,
  }), [scrollStageToTop, bottomAddonTarget])

  const [menuOpen, setMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [settingsStartTab, setSettingsStartTab] = useState<'ALERTS' | undefined>()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [overviewOpen, setOverviewOpen] = useState(false)
  const [incomingInvitesOpen, setIncomingInvitesOpen] = useState(false)
  const [signOutOpen, setSignOutOpen] = useState(false)
  const tourGuide = useContext(TourGuideContext)
  const isTourActive = Boolean(tourGuide?.isTourActive)
  const hasOverlayWithGuide = overviewOpen || incomingInvitesOpen || settingsOpen || isProfileOpen
  const activeScreenGuideId = hasOverlayWithGuide
    ? undefined
    : memberScreenGuideId(activeTab, aiMode, nexusPanel)
  const notificationRouter: NotificationRouterHandle = useMemo(() => {
    const withFallback = (name: 'openCreditsLedger' | 'openReport', action: () => void) => () => {
      if (notificationNavigators.missingNavigators?.includes(name)) {
        console.warn(`[notificationRouter] navigator missing: ${name}`)
        setOverviewOpen(true)
        return
      }
      action()
    }
    const ctx: NotificationRouterCtx = {
      ...notificationNavigators,
      workspaceSlug: workspace.slug,
      workspaces,
      openIncomingInvites: () => setIncomingInvitesOpen(true),
      openMemberSettings: (opts) => {
        setSettingsStartTab(opts?.startTab === 'ALERTS' ? 'ALERTS' : undefined)
        setSettingsOpen(true)
      },
      openProfile: () => onOpenProfile?.(),
      openOverview: () => setOverviewOpen(true),
      openCreditsLedger: withFallback('openCreditsLedger', notificationNavigators.openCreditsLedger),
      openReport: (reportId) => withFallback('openReport', () => notificationNavigators.openReport(reportId))(),
      openTeam: () => setIncomingInvitesOpen(true),
      switchToWorkspaceForTarget: (targetId, open, wsName) =>
        switchToWorkspaceForTarget(targetId, open, {
          activeWorkspaceId: workspace.id,
          workspaces,
          workspaceName: wsName,
          switchWorkspace: async (id) => {
            const target = workspaces.find((w) => w.id === id)
            await requestWorkspaceSwitch({
              toWorkspaceId: id,
              source: 'NOTIFICATION',
              landing: safeMemberLanding({ workspace: target, tab: 'for-you' }),
            })
            if (target && setAppMode) {
              setAppMode(target.kind ?? (isPersonalWorkspace(target) ? 'PERSONAL' : 'TEAM'))
            }
          },
          showToast,
          t,
        }),
      openWorkspaceTarget: (args) =>
        openWorkspaceTarget({
          ...args,
          options: {
            activeWorkspaceId: workspace.id,
            workspaces,
            switchWorkspace: async (id) => {
              const target = workspaces.find((w) => w.id === id)
              await requestWorkspaceSwitch({
                toWorkspaceId: id,
                source: 'NOTIFICATION',
                landing: safeMemberLanding({ workspace: target, tab: 'for-you' }),
              })
              if (target && setAppMode) {
                setAppMode(target.kind ?? (isPersonalWorkspace(target) ? 'PERSONAL' : 'TEAM'))
              }
            },
            showToast,
            t,
            ...args.options,
          },
        }),
      t,
    }
    return {
      resolveNotification: (notification) => resolveNotification(notification, ctx),
      resolveDigestItem: (item) => resolveDigestItem(item, ctx),
    }
  }, [notificationNavigators, onOpenProfile, setAppMode, showToast, t, workspace.id, workspace.slug, workspaces])

  useEffect(() => {
    if (!showToast) return
    setMemberOutboxNotifier((msg) => showToast(msg, 'warning'))
    return () => setMemberOutboxNotifier(null)
  }, [showToast])

  useEffect(() => {
    const header = headerRef.current
    if (!header || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height
        if (typeof height === 'number' && height > 0) {
          shellRef.current?.style.setProperty('--member-top-chrome-height', `${Math.ceil(height)}px`)
        }
      }
    })
    ro.observe(header)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const syncKeyboardState = () => setKeyboardOpen(document.body.dataset.keyboardOpen === 'true')
    syncKeyboardState()
    const observer = new MutationObserver(syncKeyboardState)
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-keyboard-open'] })
    return () => observer.disconnect()
  }, [])

  const [moreSheetOpen, setMoreSheetOpen] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)

  const [opsPhoneItems, setOpsPhoneItems] = useState<OpsPhoneItemKey[]>(() =>
    getMemberOpsPhoneItems(workspace.id),
  )

  useEffect(() => {
    return subscribeMemberOpsStore(() => {
      setOpsPhoneItems(getMemberOpsPhoneItems(workspace.id))
    })
  }, [workspace.id])

  useEffect(() => {
    const wsId = workspace.id
    const uid = currentUser?.uid
    if (!wsId || !uid) return
    const modules = workspace.modules as unknown as Record<string, boolean | undefined> | undefined
    if (!role) {
      if (getMemberOpsPhoneItems(wsId).length > 0) {
        setMemberOpsPhoneItemsForWorkspace(wsId, [])
      }
      return
    }
    try {
      const unsub = subscribeToMyOpsPhoneItems(
        wsId,
        uid,
        role,
        modules,
        (items) => {
          setMemberOpsPhoneItemsForWorkspace(wsId, items)
        },
      )
      return () => unsub()
    } catch {
      return () => undefined
    }
  }, [workspace.id, workspace.modules, currentUser?.uid, role])

  const memberTabPolicy = useMemo<TabBarPolicy>(() => ({
    opsPhoneItems,
  }), [opsPhoneItems])

  const { pinned, more, locked, moreHasUnread, isTabUnread, savePersonalTabs } = useMemberTabBar({
    uid: currentUser?.uid,
    workspaceId: workspace.id,
    role,
    workspace,
    policy: memberTabPolicy,
    useCase: workspace.onboardingSetup?.useCase || undefined,
    unreadByTab: {
      'for-you': !focusMode.active && unreadCount > 0,
    },
  })

  const isBookingsEnabled = isSectorBookingsEnabled(workspace)

  const effectivePinned = useMemo(() => {
    if (!isBookingsEnabled) return pinned
    if (pinned.length < 4 && more.length === 0) {
      return [...pinned, 'bookings' as unknown as CatalogMemberTabKey]
    }
    return pinned
  }, [pinned, more, isBookingsEnabled])

  const effectiveMore = useMemo(() => {
    if (!isBookingsEnabled) return more
    if (pinned.length >= 4 || more.length > 0) {
      return [...more, 'bookings' as unknown as CatalogMemberTabKey]
    }
    return more
  }, [pinned, more, isBookingsEnabled])

  const isTabInMore = effectiveMore.includes(activeTab as unknown as CatalogMemberTabKey)

  if (!isMobile) {
    const navItems = makeDesktopNavItems(workspace, t)
    return (
      <div className="memberShell min-h-screen bg-[var(--bg)] text-[var(--text-strong)]" data-member-shell>
        <header className="memberHeader bg-[var(--surface)] backdrop-blur-md">
          <div>
            <span className="memberEyebrow">{t('NLSMemberWorkspaceEyebrow')}</span>
            <h1>{workspace.name}</h1>
          </div>
          <div className="flex items-center gap-2">
            {activeScreenGuideId ? (
              <ScreenGuideHost
                screenId={activeScreenGuideId}
                disabled={isTourActive}
                className="screenGuideLauncher--topbar"
              />
            ) : null}
            <div className="memberHeaderMeta">
              <span>{t(roleLabelKey(role))}</span>
              <strong>{workspace.slug}</strong>
            </div>
          </div>
        </header>

        <div className="memberDesktopFrame">
          <nav className="memberIconRail bg-[var(--surface-muted)] backdrop-blur-md" aria-label={t('NLSMemberAriaNavigation')}>
            {navItems.map((item) => (
              <Pressable
                key={item.key}
                className={item.key === activeTab ? 'isActive' : undefined}
                aria-label={item.label}
                aria-pressed={item.key === activeTab}
                onClick={() => onNavigate(item.key, item.path)}
              >
                {item.icon}
                <span>{item.label}</span>
              </Pressable>
            ))}
          </nav>

          <main className="memberStage bg-[var(--surface)] backdrop-blur-md" data-testid="member-stage" ref={stageRef}>
            <MemberLayoutContext.Provider value={layoutContextValue}>
              {children}
            </MemberLayoutContext.Provider>
          </main>

          <aside className="memberSidecar bg-[var(--surface)] backdrop-blur-md" aria-label={t('NLSMemberContextEyebrow')}>
            {sidecar}
          </aside>
        </div>
      </div>
    )
  }

  const personalInMemberAppEnabled = featureFlag('personalInMemberApp')
  const memberWorkspaces = personalInMemberAppEnabled
    ? workspaces.filter((candidate) => isTeamWorkspace(candidate) || isPersonalWorkspace(candidate))
    : workspaces.filter((candidate) => isTeamWorkspace(candidate))

  const onEnterFocus = () => {
    emitSensoryEvent('MUTATION_TRIGGERED', { source: 'member-todos-tab' })
    focusMode.enter()
  }

  const tabItems: Array<MobileTabItem<CatalogMemberTabKey>> = effectivePinned.map((key) => {
    const { icon, activeIcon } = getMemberTabIcons(key)
    const isFixed = key === 'for-you'
    return {
      key,
      label: getMemberTabLabel(key as MemberTabKey, t),
      icon,
      activeIcon,
      // Phase D contract: testId: 'member-tab-todos'
      // Phase D contract: onLongPress: onEnterFocus
      testId: isFixed ? 'member-tab-todos' : `member-tab-${key}`,
      unread: isTabUnread(key),
      onLongPress: isFixed ? onEnterFocus : () => setEditorOpen(true),
    }
  })

  return (
    <div
      ref={shellRef}
      className="memberMobileShell h-[100dvh] min-h-[100svh] overflow-hidden bg-[var(--bg)] text-[var(--text-strong)] flex flex-col"
      data-member-shell
      data-active-tab={activeTab}
      data-has-secondary-tabs={secondaryTabs ? 'true' : 'false'}
      style={{
        // Keep the scrollable stage above the fixed tab stack. The secondary
        // row is compact, but two rows plus safe-area still needs a larger
        // reserved viewport band than a content-only bottom padding. These
        // first-paint values match the tightened 48px/56px rows plus the 1px
        // flat-screen safe-area gap; ResizeObserver replaces them precisely.
        ['--member-bottom-chrome-height' as string]: keyboardOpen
          ? '0px'
          : measuredBottomChromeHeight !== null
            ? `${Math.ceil(measuredBottomChromeHeight)}px`
            : secondaryTabs ? '6.625rem' : '3.625rem',
        // The header owns the top safe area. Its first-paint estimate is the
        // safe-area-aware top padding plus the 40px action row and 12px bottom padding.
        ['--member-top-chrome-height' as string]: 'calc(max(0.75rem, env(safe-area-inset-top, 0px)) + 3.25rem)',
        ['--member-top-chrome-gap' as string]: '8px',
      }}
    >
      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-30 bg-[color-mix(in_srgb,var(--surface)_85%,transparent)] backdrop-blur-3xl border-b border-[var(--border-subtle)] px-3 py-3 flex items-center gap-2"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <HeaderIconButton
          onClick={() => setMenuOpen(true)}
          label={t('NLSMemberAriaOpenMenu')}
          testId="member-menu-trigger"
        >
          <MenuRoundedIcon style={{ fontSize: '22px' }} />
        </HeaderIconButton>
        {isTabInMore ? (
          <div className="flex-1 min-w-0 flex items-center px-1" data-testid="member-header-more-title">
            <h1 className="text-base font-semibold truncate text-[var(--text-strong)]">
              {getMemberTabLabel(activeTab, t)}
            </h1>
          </div>
        ) : (
          <MemberWorkspaceSwitcher
            workspaces={memberWorkspaces}
            activeWorkspaceId={workspace.id}
            activeRole={role}
            onSelect={(next) => onSelectWorkspace?.(next)}
          />
        )}
        {onOpenSyncCenter ? (
          <MobileSyncStatusPill role="MEMBER" onOpen={onOpenSyncCenter} className="shrink-0" />
        ) : null}
        {activeScreenGuideId ? (
          <ScreenGuideHost
            screenId={activeScreenGuideId}
            disabled={isTourActive}
            className="screenGuideLauncher--topbar"
          />
        ) : null}
        <HeaderIconButton
          onClick={() => setNotificationsOpen(true)}
          label={t('NLSMemberAriaOpenNotifications')}
          unreadLabel={t('NLSMemberAriaUnread')}
          showDot={!focusMode.active && unreadCount > 0}
          testId="member-notifications-trigger"
        >
          {!focusMode.active && unreadCount > 0 ? (
            <NotificationsActiveRoundedIcon
              style={{ fontSize: '22px' }}
            />
          ) : (
            <NotificationsNoneRoundedIcon style={{ fontSize: '22px' }} />
          )}
        </HeaderIconButton>
      </header>

      <main
        ref={stageRef}
        className={`memberMobileStage min-h-0 flex-1 overscroll-contain px-4 py-4 flex flex-col gap-4 ${
          activeTab === 'chat' ? 'overflow-hidden' : 'overflow-y-auto'
        }`}
        data-testid="member-stage"
        style={{
          paddingTop: activeTab === 'chat'
            ? 'var(--member-top-chrome-height, 5.75rem)'
            : 'calc(var(--member-top-chrome-height, 5.75rem) + var(--member-top-chrome-gap, 8px))',
          scrollPaddingTop: 'calc(var(--member-top-chrome-height, 5.75rem) + var(--member-top-chrome-gap, 8px))',
          paddingBottom: activeTab === 'chat'
            ? 'var(--member-bottom-chrome-height, 3.625rem)'
            : activeTab === 'ai'
            ? 'calc(max(var(--keyboard-dock-height, 0px) + 12px, var(--member-bottom-chrome-height, 3.625rem) - env(safe-area-inset-bottom, 0px)))'
            : 'calc(var(--member-bottom-chrome-height, 3.625rem) + 1rem)',
          scrollPaddingBottom: activeTab === 'chat'
            ? 'var(--member-bottom-chrome-height, 3.625rem)'
            : activeTab === 'ai'
            ? 'calc(max(var(--keyboard-dock-height, 0px) + 12px, var(--member-bottom-chrome-height, 3.625rem) - env(safe-area-inset-bottom, 0px)))'
            : 'calc(var(--member-bottom-chrome-height, 3.625rem) + 1rem)',
          touchAction: 'pan-y',
        }}
      >
        <MemberLayoutContext.Provider value={layoutContextValue}>
          {children}
        </MemberLayoutContext.Provider>
      </main>

      <MobileTabBar
        ariaLabel={t('NLSMemberAriaNavigation')}
        testId="member-bottom-nav"
        hasSecondaryRow={Boolean(secondaryTabs)}
        surface={activeTab}
        addon={<div ref={setBottomAddonTarget} className="memberBottomAddonSlot empty:hidden" />}
        onHeightChange={(height) => {
          setMeasuredBottomChromeHeight(height)
          shellRef.current?.style.setProperty('--member-bottom-chrome-height', `${Math.ceil(height)}px`)
        }}
      >
        {secondaryTabs}
        <MobileTabRow
          items={tabItems}
          activeKey={
            isTabInMore
              ? undefined
              : activeTab === 'calendar' || activeTab === 'chat'
                ? 'nexus'
                : activeTab === 'you'
                  ? 'board'
                  : (activeTab as unknown as CatalogMemberTabKey)
          }
          onChange={(key) => onNavigate(key as MemberTabKey, buildMemberPath({ workspace, tab: key as MemberTabKey }))}
          ariaLabel={t('NLSMemberAriaTabs')}
          more={
            effectiveMore.length > 0
              ? {
                  label: t('NLSCommonTabMore'),
                  isActive: isTabInMore,
                  unread: moreHasUnread,
                  onClick: () => setMoreSheetOpen(true),
                  testId: 'member-tab-more',
                }
              : undefined
          }
        />
      </MobileTabBar>

      <MoreTabsSheet
        isOpen={moreSheetOpen}
        onClose={() => setMoreSheetOpen(false)}
        tabs={effectiveMore.map((key) => {
          const { icon } = getMemberTabIcons(key)
          const lockedItem = locked.find((l) => l.key === key)
          return {
            key,
            label: getMemberTabLabel(key as MemberTabKey, t),
            icon,
            unread: isTabUnread(key),
            locked: Boolean(lockedItem),
            lockReason: lockedItem ? t(lockedItem.reasonKey) : undefined,
            testId: `member-more-tab-${key}`,
          }
        })}
        onSelectTab={(key) => {
          setMoreSheetOpen(false)
          onNavigate(key as MemberTabKey, buildMemberPath({ workspace, tab: key as MemberTabKey }))
        }}
        onOpenEdit={() => {
          setMoreSheetOpen(false)
          setEditorOpen(true)
        }}
      />

      <TabBarEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        pinned={pinned}
        more={more}
        locked={locked.map((l) => ({ key: l.key as CatalogMemberTabKey, reason: t(l.reasonKey) }))}
        labels={(key) => getMemberTabLabel(key as MemberTabKey, t)}
        icons={(key) => getMemberTabIcons(key as CatalogMemberTabKey).icon}
        onSave={(next) => void savePersonalTabs(next as CatalogMemberTabKey[])}
        onResetToDefault={() => void savePersonalTabs([])}
      />

      <MemberMenuDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        displayName={displayName}
        identity={currentUser?.email ?? null}
        avatarUrl={profileImageUrl || null}
        onOpenOverview={canPerform('overview.view') ? () => setOverviewOpen(true) : undefined}
        onOpenProfile={() => onOpenProfile?.()}
        onOpenIncomingInvites={() => setIncomingInvitesOpen(true)}
        onOpenSettings={() => setSettingsOpen(true)}
        onEnterFocus={() => focusMode.enter()}
        onSignOut={() => setSignOutOpen(true)}
        workspaces={memberWorkspaces}
        activeWorkspaceId={workspace.id}
        activeRole={role}
        unreadCount={unreadCount}
        onSelectWorkspace={(ws) => onSelectWorkspace?.(ws)}
        onOpenSyncCenter={() => onOpenSyncCenter?.()}
      />
      <FocusModeOverlay />
      {overviewOpen ? (
        <SideNavPageShell
          title={t('NLSMemberPanelOverview')}
          uiName="UI-MPCAP-MOVR"
          screenGuideId="member.overview"
          onClose={() => setOverviewOpen(false)}
        >
          <OverviewPhonePage variant="member" onBack={() => setOverviewOpen(false)} />
        </SideNavPageShell>
      ) : null}
      {incomingInvitesOpen ? (
        <SideNavPageShell
          title={t('NLSMemberPanelIncomingInvites')}
          uiName="UI-MPCAP-MINV"
          screenGuideId="member.incoming-invites"
          onClose={() => setIncomingInvitesOpen(false)}
        >
          <IncomingInvitesPage roleHint="MEMBER" />
        </SideNavPageShell>
      ) : null}
      <MemberNotificationDrawer
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        notificationRouter={notificationRouter}
        onOpenSettings={() => { setSettingsStartTab('ALERTS'); setSettingsOpen(true) }}
      />
      {settingsOpen ? (
        <SideNavPageShell
          title={t('NLSMemberPanelSettings')}
          eyebrow={t('NLSMemberPanelCommandPreferences')}
          uiName="UI-MPCAP-MSET"
          screenGuideId="member.settings"
          onClose={() => setSettingsOpen(false)}
        >
          <MobileSettingsCommandCenter
            role="MEMBER"
            displayName={displayName}
            identity={currentUser?.email ?? null}
            workspaceName={workspace.name}
            workspaceRole={role}
            workspaceCount={memberWorkspaces.length}
            pendingSyncCount={0}
            failedSyncCount={0}
            unreadCount={unreadCount}
            initialSection={settingsStartTab}
            onOpenProfile={() => {
              setSettingsOpen(false)
              onOpenProfile?.()
            }}
            onOpenIncomingInvites={() => {
              setSettingsOpen(false)
              setIncomingInvitesOpen(true)
            }}
            onSignOut={() => setSignOutOpen(true)}
          />
        </SideNavPageShell>
      ) : null}
      <ConfirmActionModal
        open={signOutOpen}
        title="Sign out?"
        body="You'll need to sign in again to open Timelining."
        confirmLabel="Sign out"
        cancelLabel="Cancel"
        danger
        onClose={() => setSignOutOpen(false)}
        onConfirm={() => { setSignOutOpen(false); void logout() }}
      />
    </div>
  )
}
